import * as React from 'react'
import type { Language } from 'codegen/types'
import { defaultLanguage } from 'cf-i18n/constants'
import { useStudioLangCookie } from 'cf-i18n/cookies'

/**
 * Whether the `lang` cookie should be rewritten to the resolved language.
 *
 * Two cases are deliberately left alone. A reader whose cookie already holds the
 * resolved language needs no write. A reader with no cookie at all who resolves
 * to English has expressed no preference — the resolver returns English as the
 * fallback, not as a choice — and minting a cookie for them would record one
 * they never made.
 */
export function shouldPersistStudioLanguageCookie(
  language: Language,
  cookieLanguage: Language | null,
): boolean {
  if (language === cookieLanguage) {
    return false
  }

  return cookieLanguage !== null || language !== defaultLanguage
}

interface UsePersistStudioLanguageCookieOptions {
  /** When false, skips the write until URL/account language resolution is complete. */
  enabled?: boolean
}

/**
 * Records the resolved language in the `lang` cookie.
 *
 * The account is the source of truth once a reader is logged in, but only the
 * cookie travels with the request, so a server that has to pick a locale before
 * React runs can read nothing else. Leaving the two to disagree is what makes a
 * logged-in reader land on someone else's locale: `middleware-utils`'
 * `getLocaleRedirect` sees a stale cookie — or none — and serves the URL as it
 * stands, while the client resolves the account language and renders the picker
 * against that.
 *
 * Mount this alongside `useSyncLocaleUrl` in any app whose server redirects on
 * the cookie. Without it the two fight: the client rewrites the prefix to the
 * account language, the next request carries the old cookie, and the server
 * sends the reader straight back.
 */
export function usePersistStudioLanguageCookie(
  language: Language | null | undefined,
  options?: UsePersistStudioLanguageCookieOptions,
) {
  const [cookieLanguage, setLangCookie] = useStudioLangCookie()
  const enabled = options?.enabled ?? true

  React.useEffect(() => {
    if (!enabled || !language) {
      return
    }

    if (!shouldPersistStudioLanguageCookie(language, cookieLanguage)) {
      return
    }

    setLangCookie(language)
  }, [cookieLanguage, enabled, language, setLangCookie])
}
